import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { AuthResources } from '@core/resources/auth.resources';
import { AuthState } from '@core/states/auth.state';
import { UserTypes } from '@core/typings/client-user.typing';
import { environment } from '@environment';
import { ClientPickerModalComponent } from '@features/auth/client-picker-modal/client-picker.modal.component';
import { I18nService } from '@yourcause/common/i18n';
import { LogService } from '@yourcause/common/logging';
import { ConfirmAndTakeActionService, ModalFactory } from '@yourcause/common/modals';
import { NotifierService } from '@yourcause/common/notifier';
import { AttachYCState, BaseYCService } from '@yourcause/common/state';
import { BbidLinkFailedReason, BbidLinkingResult, ClientForUser, DetermineSignInFlow, LinkToBbidPayload, LoginScenario } from '../typings/auth.typing';
import { BBIDService } from './bbid.service';
import { DeepLinkingService } from './deep-linking.service';
import { PortalDeterminationService } from './portal-determination.service';
import { SpinnerService } from './spinner.service';
import { SSOService } from './sso.service';
import { AuthBehaviors } from './token/token-behaviors';
import { TokenService } from './token/token.service';
import { TokenContent } from '@core/typings/token.typing';
import { LoginMethod } from '@core/typings/login-method.typing';
import { ClientUserService } from '@features/client-user/client-user.service';
import { ApplicantService } from '@core/services/auth-user/applicant.service';
export const REMEMBER_ME = 'remember-me';

@AttachYCState(AuthState)
@Injectable({ providedIn: 'root' })
export class AuthService extends BaseYCService<AuthState> {

  constructor (
    private deepLinkingService: DeepLinkingService,
    private behavior: AuthBehaviors,
    private authResources: AuthResources,
    private tokenService: TokenService,
    private portal: PortalDeterminationService,
    private i18n: I18nService,
    private confirmAndTakeActionService: ConfirmAndTakeActionService,
    private notifier: NotifierService,
    private logger: LogService,
    private modalFactory: ModalFactory,
    private spinner: SpinnerService,
    private router: Router,
    private bbidService: BBIDService,
    private ssoService: SSOService,
    private clientUserService: ClientUserService,
    private applicantService: ApplicantService
  ) {
    super();
  }

  get username () {
    return this.get('username');
  }

  get loginScenario () {
    return this.get('loginScenario');
  }

  get signInFlow () {
    return this.get('signInFlow');
  }

  setSignInFlow (flow: DetermineSignInFlow) {
    this.set('signInFlow', flow);
  }

  setLoginScenario (scenario: LoginScenario) {
    this.set('loginScenario', scenario);
  }

  setUsername (username: string) {
    this.set('username', username);
  }

  getBlackbaudLoginRoute () {
    return this.authResources.getBlackbaudLoginRoute();
  }

  acceptTermsOfService () {
    return this.authResources.acceptTermsOfService();
  }

  /**
   * Handles Initial Login By Determing the Scenario
   *
   * @param email: Email to determine sign in flow
   * @returns the login scenario
   */

  /**
   * Handles Initial Login By Determing the Scenario
   *
   * @param email: Email to determine sign in flow
   * @returns the login scenario
   */
  async doInitialLogin (email = this.username) {
    let scenario: LoginScenario;
    const subdomain = this.portal.getCurrentPrefix();
    const response = await this.authResources.determineSignInFlow(email, subdomain);
    if (!!response) {
      this.setSignInFlow(response);
      if (
        !!this.ssoService.ssoConfig?.ssoLoginUrl &&
        (response.isSSO || !!response.ssoId)
      ) {
        scenario = LoginScenario.SSO_USER;
      } else if (
        response.isBbidLinked || response.hasExistingBbidAccount
      ) {
        // If BBID is already linked or the user has an existing BBID account
        scenario = LoginScenario.EXISTING_BBID_USER;
      } else if (!response.emailExists) {
        scenario = LoginScenario.UNKNOWN_EMAIL;
      } else {
        // If BBID is enabled but not linked and they do not have a BBID account
        scenario = LoginScenario.NEW_BBID_USER;
      }
    } else {
      this.setSignInFlow({
        isBbidLinked: false,
        hasExistingBbidAccount: false,
        emailExists: false,
        ssoId: null,
        isSSO: false
      });
      scenario = LoginScenario.FAIL;
    }
    this.setLoginScenario(scenario);

    return scenario;
  }

  /**
   * Handles Initial Login Step After Email Entered
   *
   * @returns the login scenario to proceed with
   */
  async handleInitialLogin () {
    const response = await this.confirmAndTakeActionService.genericTakeAction(
      () => this.doInitialLogin(),
      '',
      this.i18n.translate(
        'AUTH:errorLoggingIn',
        {},
        'There was an error logging in'
      )
    );
    if (response.passed) {
      return response.endpointResponse;
    }  else {
      this.setLoginScenario(LoginScenario.FAIL);

      return LoginScenario.FAIL;
    }
  }

  /**
   * Handle linking BBID account to user
   *
   * @param inSystem: Is the user already in the system?
   * @param gcEmail: User's GC email
   * @param mfaToken: MFA token
   * @param grantProgramGuid: Program guid to apply for
   * @returns the linking result and the fail reason
   */
  async handleLinkingBbidAccount (
    inSystem: boolean,
    clientId: number,
    gcEmail?: string,
    mfaToken?: string,
    grantProgramGuid?: string
  ): Promise<{
    linkingResult: BbidLinkingResult;
    failReason: BbidLinkFailedReason;
  }> {
    const isBrandNewAccountNoEmail = !inSystem && !gcEmail;
    const token = await this.bbidService.getToken();
    const parsed = this.tokenService.parseJwt(token);
    if (isBrandNewAccountNoEmail) {
      gcEmail = parsed.email;
    }
    if (!mfaToken && gcEmail?.toLowerCase() !== parsed.email?.toLowerCase()) {
      // First check if we already have an account for the Blackbaud Email
      const subdomain = this.portal.getCurrentPrefix();
      const response = await this.authResources.determineSignInFlow(parsed.email, subdomain);
      if (response.emailExists) {
        if (response.isBbidLinked) {
          return {
            linkingResult: BbidLinkingResult.FAILED,
            failReason: BbidLinkFailedReason.BBID_LINKED_TO_ANOTHER_ACCOUNT
          };
        } else {
          return {
            linkingResult: BbidLinkingResult.FAILED,
            failReason: BbidLinkFailedReason.BBID_NON_LINKED_EMAIL_EXISTS_IN_GC
          };
        }
      }
      // This means the user signed up with a different email for BBID than their GrantsConnect email
      // In order to link, they need MFA for verification
      let proceed = true;
      if (!inSystem) {
        // If the applicant was invited to apply, they will not be in the system but will have an email
        // If the emails differ, we need to follow these steps:
        // 1. Create applicant account for user
        // 2. Trigger MFA email and have the user confirm the original "invite" email
        // 3. Link the accounts after successful MFA
        proceed = await this.createApplicantAccountForBbid(
          parsed.given_name,
          parsed.family_name,
          gcEmail,
          this.i18n.language,
          grantProgramGuid
        );
      }
      if (proceed) {
        this.logger.log('User signed up with different BBID email', {
          gcEmail,
          bbidEmail: parsed.email
        });

        return {
          linkingResult: BbidLinkingResult.NEED_TO_SEND_CODE,
          failReason: null
        };
      } else {
        this.logger.log('BBID - create applicant account for BBID failed', {
          gcEmail,
          bbidEmail: parsed.email
        });

        return {
          linkingResult: BbidLinkingResult.FAILED,
          failReason: BbidLinkFailedReason.APPLICANT_CREATE_FAILED
        };
      }
    } else {
      // Brand new accounts don't require linking through the endpoint
      let wasLinked = !inSystem;
      let linkFailReason: BbidLinkFailedReason = null;
      if (!wasLinked) {
        const {
          passed,
          failReason
        } = await this.linkToBbidAccount(gcEmail, mfaToken);
        wasLinked = passed;
        linkFailReason = failReason;
      }
      if (wasLinked) {
        let deepLink = '';
        if (!!grantProgramGuid) {
          deepLink = `/apply/programs/${grantProgramGuid}`;
        }
        if (!clientId) {
          clientId = await this.getSelectedClientForUser(this.username, parsed?.sub);
        }
        const passed = await this.loginWithBbid(
          environment.bbidGcServiceId,
          environment.bbidGcEnvironmentId,
          true,
          true,
          deepLink,
          true,
          clientId
        );

        if (passed) {
          return {
            linkingResult: BbidLinkingResult.LINKED,
            failReason: null
          };
        } else {
          return {
            linkingResult: BbidLinkingResult.FAILED,
            failReason: BbidLinkFailedReason.BBID_LOGIN_FAILED
          };
        }
      } else {
        return {
          linkingResult: BbidLinkingResult.FAILED,
          failReason: linkFailReason || BbidLinkFailedReason.LINK_FAILED_UNKNOWN
        };
      }
    }
  }

  /**
   * Link GC account to BBID Account
   *
   * @param gcEmail: GC email
   * @param mfaToken: MFA token
   * @returns if the link was successful and failure reason if it fails
   */
  async linkToBbidAccount (
    gcEmail: string,
    mfaToken?: string
  ): Promise<{
    passed: boolean;
    failReason: BbidLinkFailedReason;
  }> {
    try {
      const payload: LinkToBbidPayload = {
        email: gcEmail,
        bbidToken: await this.bbidService.getToken(),
        userType: this.portal.getUserType(),
        mfaToken
      };
      await this.authResources.linkToBbidAccount(payload);
      this.logger.log('BBID linking successful', {
        email: gcEmail
      });

      return {
        passed: true,
        failReason: null
      };
    } catch (e) {
      this.logger.error(e);
      const error = e as HttpErrorResponse;
      if (error?.error?.message === 'BBID is already associated with an account.') {
        this.logger.log('BBID linking failed - BBID is already associated with an account', {
          email: gcEmail
        });

        return {
          passed: false,
          failReason: BbidLinkFailedReason.BBID_LINKED_TO_ANOTHER_ACCOUNT
        };
      } else if (error?.error?.message === 'A valid mfa token is required when emails do not match.') {
        this.logger.log('BBID linking failed - Emails were different and MFA code is wrong', {
          email: gcEmail,
          mfaToken
        });

        return {
          passed: false,
          failReason: BbidLinkFailedReason.INVALID_MFA_DIFFERENT_EMAILS
        };
      } else {
        this.notifier.error(this.i18n.translate(
          'AUTH:textErrorLinkingBbidAccount',
          {},
          'There was an error linking your Blackbaud ID account'
        ));
        this.logger.log('BBID linking failed unknown', {
          email: gcEmail
        });
      }

      return {
        passed: false,
        failReason: BbidLinkFailedReason.LINK_FAILED_UNKNOWN
      };
    }
  }

  /**
   * Create applicant account for BBID user who uses different email to sign up on BBID than invited with
   *
   * @param firstName: Applicant first name
   * @param lastName: Applicant last name
   * @param email: Applicant email
   * @param languageCulture: Applicant language
   * @param grantProgramGuid: Program guid to apply for
   * @returns if the account creation passed
   */
  async createApplicantAccountForBbid (
    firstName: string,
    lastName: string,
    email: string,
    languageCulture: string,
    grantProgramGuid: string
  ) {
    try {
      await this.authResources.createApplicantAccountForBbid(
        firstName,
        lastName,
        email,
        languageCulture,
        grantProgramGuid
      );

      return true;
    } catch (e) {
      this.logger.error(e);

      return false;
    }
  }

  /**
   * Login with BBID
   *
   * @param svcId: Service ID
   * @param envId: Environment ID
   * @param attemptDeepLink: Attempt to deep link after login
   * @param logoutOnFail: Logout if it fails?
   * @param deepLink: Deep link to navigate to
   * @param isInitialSignin: is this the initial sign in? (not refresh of page)
   * @returns if the BBID login was successful or not
   */
  async loginWithBbid (
    svcId: string,
    envId: string,
    attemptDeepLink: boolean,
    logoutOnFail: boolean,
    deepLink: string,
    isInitialSignin: boolean,
    clientId: number,
    fetchClientIdIfNonePassed = false
  ) {
    let isSuccessful: boolean;
    let token: TokenContent;

    try {
      const isGcBbid = this.bbidService.isGrantsConnectBbid(svcId, envId);
      const isBbgm = svcId && envId && !isGcBbid;
      await this.tokenService.setBbidJwt(svcId, envId, false, isBbgm, clientId);
      token = this.tokenService.parseJwt();
      if (!clientId && fetchClientIdIfNonePassed) {
        clientId = await this.getSelectedClientForUser(token.email, token.sub);
        if (!!clientId) {
          this.tokenService.updateClientId(clientId);
        }
      }
      const isValidBbid = await this.verifyBbidLink();
      if (isValidBbid) {
        isSuccessful = true;
        await this.bbidService.loadOmnibar(svcId, envId);
        const attributes: Record<string, string> = {
          isBbgm: '' + isBbgm,
          email: token?.email
        };
        if (isInitialSignin) {
          this.logger.log('Successful BBID sign in', attributes);
          await this.authResources.trackBbidLogin(clientId);
        }
      } else {
        isSuccessful = false;
        this.logger.log('BBID login failed with invalid from endpoint - VerifyBbidLink', {
          email: token?.email
        });
      }
    } catch (e) {
      isSuccessful = false;
      this.logger.error(e);
      token = this.tokenService.parseJwt();
      this.logger.log('BBID login failed from unknown', {
        email: token?.email
      });
    }
    
    // track event and identify user before routing
    const jwt = this.tokenService.parseJwt();
    let userId = null;
    if (isSuccessful) {
      userId = this.portal.getUserType() === UserTypes.MANAGER
        ? (await this.clientUserService.getUser(true)).userId.toString()
        : (await this.applicantService.getApplicant(true)).userId.toString();
    }
    this.tokenService.trackInitialLoginData(
      LoginMethod.BBID, 
      isSuccessful,
      userId,
      jwt?.email,
      clientId
    );

    if (isSuccessful && attemptDeepLink) {
      this.deepLinkingService.attemptDeepLink(deepLink || this.behavior.current.postLoginRedirect, {
        alreadyLoggedInWithBbid: true
      });
    }

    if (!isSuccessful && logoutOnFail) {
      await this.tokenService.logout();
    }
    
    return isSuccessful;
  }

  /**
   * Handles logic needed for new users coming to the system who already have an account but no password / bbid link
   *
   * @param inSystem: Is the user in the GC system?
   * @param email: Users email
   */
  async handleNewUserSlatedForBbid (
    inSystem: boolean,
    email: string
  ) {
    try {
      if (inSystem) {
        await this.handleBbidForNewUserInSystem(email);
      } else {
        await this.handleBbidForNewUserNotInSystem(email);
      }
    } catch (e) {
      this.logger.error(e);
      this.notifier.error(this.i18n.translate(
        'AUTH:textErrorSettingUpAccount',
        {},
        'There was an error setting up your account'
      ));
      this.router.navigate([this.portal.getRoute('auth/signin')]);
    }
  }

  /**
   * When a Grant Manager or Applicant gets created
   * We need to send them to BBID to link an account
   *
   * @param email: Email for new user
   */
  async handleBbidForNewUserInSystem (email: string) {
    await this.doInitialLogin(email);
    this.bbidService.redirectToBbid(this.signInFlow, email, false);
  }

  /**
   * This logic only applies for applicant invitations.
   * Redirects the user to BBID with a return URL that will allow them to get their account created.
   *
   * @param email: Email of user
   */
  async handleBbidForNewUserNotInSystem (
    email: string
  ) {
    // Fire this to determine user login scenario - existing bbid or not
    await this.doInitialLogin(email);
    this.bbidService.redirectToBbid(this.signInFlow, email, true);
  }

  /**
   * Check if the user logged in with a valid BBID for the GC account
   *
   * @returns true if the BBID the  user selected is valid and they can proceed
   */
  async verifyBbidLink () {
    try {
      await this.authResources.verifyBbidLink();

      return true;
    } catch (e) {
      this.logger.error(e);

      return false;
    }
  }

  doRedirect (href: string) {
    location.href = href;
  }

  async handleBlackbaudSsoRedirect (route: ActivatedRouteSnapshot) {
    // if trying to get onto the platform tools from localhost
    const isLocal = environment.actualLocationBase === 'localhost';
    if (isLocal) {
      // redirect to QA and indicate that we are trying to get back to localhost
      const locationBase = environment.locationBase === 'localhost' ? 'yourcausegrantsqa.com' : environment.locationBase;
      this.doRedirect(
        `https://www.${locationBase}/platform/auth/signin?platformHostRename=${encodeURIComponent('localhost:51851')}`
      );

      return;
    }
    if (environment.locationBase === 'dev-grantsconnect-ui.azurewebsites.net') {
      this.doRedirect(
        `https://www.yourcausegrantsqa.com/platform/auth/signin?platformHostRename=${encodeURIComponent('dev-grantsconnect-ui.azurewebsites.net')}`
      );

      return;
    }

    // check for redirect
    const potentialRedirect = route.queryParams.platformHostRename;
    if (potentialRedirect) {
      // remember this in order to perform redirect after SSO redirect
      sessionStorage.setItem('platformHostRename', potentialRedirect);
    }

    const response = await this.getBlackbaudLoginRoute();

    this.doRedirect(response.signInUrl);
  }

  /**
   * Get Selected Client for User
   *
   * @param email: Email
   * @param blackbaudId: Blackbaud ID ("sub" attr on token)
   * @returns the client for the selected user
   */
  async getSelectedClientForUser (
    email: string,
    blackbaudId: string
  ) {
    let clientId = null;
    if (this.portal.isManager) {
      if (!!email || !!blackbaudId) {
        const associatedClients = await this.authResources.getClientsForUser(email, blackbaudId);
        clientId = associatedClients[0]?.clientId;
        if (associatedClients.length > 1) {
          let foundClient: ClientForUser;
          // If they're on a subdomain, we can infer the client
          if (this.portal.hasSubdomain()) {
            const subdomain = this.portal.getCurrentPrefix();
            foundClient = associatedClients.find((client) => {
              return client.subDomain?.toLowerCase() === subdomain?.toLowerCase();
            });
          }
          if (!!foundClient) {
            clientId = foundClient.clientId;
          } else {
            const wasLoading = this.spinner.loading;
            if (wasLoading) {
              this.spinner.stopSpinner();
            }
            clientId = await this.modalFactory.open(
              ClientPickerModalComponent,
              {
                associatedClients
              }
            );
            if (wasLoading) {
              this.spinner.startSpinner();
            }
          }
        }
      }
    }

    return clientId;
  }

  async sendVerificationCode (
    email: string,
    userType: UserTypes,
    isResend: boolean,
    clientId: number
  ) {
    try {
      await this.authResources.sendVerificationCode(email, userType, clientId);
      if (isResend) {
        this.notifier.success(this.i18n.translate(
          'common:notificationSuccessResendSecurityCode',
          {},
          'Successfully resent security code'
        ));
      }

      return true;
    } catch (e) {
      this.logger.error(e);
      this.notifier.error(this.i18n.translate(
        !isResend ?
          'common:notificationErrorSendSecurityCode' :
          'common:notificationErrorResendSecurityCode',
        {},
        !isResend ?
          'There was an error sending the security code' :
          'There was an error resending the security code'
      ));

      return false;
    }
  }
}

